<script setup>
import {ref, reactive, computed, watch, onMounted, toRaw, onBeforeUnmount} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useMainOrdersStore} from '@/stores/mainOrders.js'
import {useConfigStore} from '@/stores/config.js'
import {numeric, required, maxLength, helpers} from '@vuelidate/validators';
import {useVuelidate} from '@vuelidate/core';
import Tooltip from "@/components/Tooltip/index.vue";
import Loading from "@/components/Icon/Loading.vue";
import {Icon} from "@iconify/vue";
import {showMessage} from "@/utils/showMessage.js";
import showAlert from "@/utils/showAlert.js";
import {TabGroup, TabList, Tab, TabPanels, TabPanel} from "@headlessui/vue";
import PersianDate from "@alireza-ab/persian-date";
import DatePicker from 'primevue/datepicker';
import serversModal from "@/views/orders/Details/servers.vue"

/* tabs */
const tabs = [
  {
    title: "Details",
  }
];
const selectedTab = ref(0)

/* attachments */
const attachments = ref([])

/* Auxiliary keys allowed for all fields */
const helperKeys = [
  'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
  'ArrowLeft', 'ArrowRight', 'Home', 'End'
]

/* ---------- Stores ---------- */
const route = useRoute()
const router = useRouter()
const ordersStore = useMainOrdersStore()
const configStore = useConfigStore()

/* ---------- Loading status ---------- */
const loading = ref(true)
const saving = ref(false)

/* ---------- The field that the user last edited. ---------- */
//const activeField = ref(null) // 'price' | 'tomanPrice' | null

/* ---------- Order object ---------- */
const order = reactive({
  game: '',
  server: '',
  account: '',
  side: '',
  amount: '',
  price: '',
  value: '',
  effectiveTime: '',
  notes: '',
})

const rules = computed(() => {
  return {
    game: {required},
    server: {required},
    account: {required},
    side: {required},
    amount: {required, numeric},
    price: {required, numeric},
    value: {required},
    effectiveTime: {required},
  };
});
const $vulidate = useVuelidate(rules, order);

/* ---------- Related to server ---------- */
const isShowServersModal = ref(false)

function openServersModal() {
  if (servers.value.length > 0) {
    isShowServersModal.value = true
  }
}

function closeServersModal() {
  isShowServersModal.value = false
}

function selectServer(server) {
  order.server = server
  closeServersModal()
}

/* ---------- Related to side ---------- */
const isSideOpen = ref(false)
const sideDropdownRef = ref(null)
const sides = ['Deposit', 'Withdraw']
const sidesColorsMap = {
  Deposit: 'bg-[#4CAF50]',
  Withdraw: 'bg-[#F44336]',
}
const toggleSideDropdown = () => isSideOpen.value = !isSideOpen.value

const selectSide = (state) => {
  order.side = state
  isSideOpen.value = false
}

const handleClickOutside_sideDropdown = (e) => {
  if (sideDropdownRef.value && !sideDropdownRef.value.contains(event.target)) isSideOpen.value = false
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside_sideDropdown)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside_sideDropdown)
})

/* ---------- Related to effectiveTime ---------- */
const effectiveTimeModel = computed({
  get() {
    if (order.effectiveTime && !isNaN(order.effectiveTime)) {
      return new Date(Number(order.effectiveTime));
    }
    return null;
  },
  set(value) {
    if (value instanceof Date) {
      order.effectiveTime = value.getTime();
    } else {
      order.effectiveTime = '';
    }
  }
})

function selectNow() {
  const now = new Date();
  order.effectiveTime = now.getTime();
}

function clearEffectiveTime() {
  order.effectiveTime = '';
}

/* ---------- Select options ---------- */
const games = computed(() => configStore.getGamesByService('StocksManager') || [])

const selectedGame = computed(() => (route.params.game ? games.value.find(g => g.abbreviation === route.params.game) : games.value.find(g => g.game === order.game)) || null)

const servers = computed(() =>
    selectedGame.value ? selectedGame.value.servers : []
)
const accounts = computed(() => {
  return order.game ? ordersStore.getGameAccountByGame(order.game) || [] : []
})

/* ---------- Selected account and its title ---------- */
const selectedAccountObj = computed(() =>
    accounts.value.find(a => a.id === order.account) || null
)

const accountTitle = computed(() =>
    selectedAccountObj.value ? selectedAccountObj.value.title : ''
)

function convertTimestamp(timestamp) {
  let p = new PersianDate(Number(timestamp));
  return p.toString('YYYY-MM-DD-HH-mm');
}

/* Digit only (no dots) */
function allowDigits(e) {
  if (
      helperKeys.includes(e.key) ||
      // Allow Ctrl+C / Ctrl+V / Ctrl+A and…:
      (e.ctrlKey || e.metaKey)
  ) return

  if (!/^[0-9]$/.test(e.key)) e.preventDefault()
}

/* Digit + a single dot  */
function allowDigitsDot(e) {
  if (
      helperKeys.includes(e.key) ||
      (e.ctrlKey || e.metaKey)
  ) return

  if (e.key === '.') {
    if (e.target.value.includes('.')) e.preventDefault()
    return
  }

  if (!/^[0-9]$/.test(e.key)) e.preventDefault()
}

/* ---------- Reaction to Game Change ---------- */
function onGameChange() {
  order.server = route.params.server ? route.params.server : servers.value.length === 1 ? servers.value[0] : ''
  order.account = accounts.value.length === 1 ? accounts.value[0].id : ''
}

function trimDecimals(num, decimals) {
  return num.toFixed(decimals).replace(/\.?0+$/, '')
}

function formatNumber(val, maxDecimals = 0) {
  if (val === '' || val === null || val === undefined) return ''
  const num = parseFloat(val.toString().replace(/,/g, ''))
  if (isNaN(num)) return ''
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  })
}


/* ---------- Inputs ---------- */
function onAmountInput(raw) {
  order.amount = sanitizeNumber(raw)
  recalc()
}

function onPriceInput(raw) {
  const cleaned = sanitizeNumber(raw)
  if (cleaned === '') {
    order.price = ''
  } else {
    order.price = cleaned // no trimDecimals — save with all decimals
  }

  /*if (order.price && parseFloat(order.usd_tmn)) {
    order.tomanPrice = (parseFloat(order.price) * parseFloat(order.usd_tmn)).toString()
  } else {
    order.tomanPrice = ''
  }*/

  recalc()
}


/* ---------- Related to effective time input ---------- */
function resetCreatedFrom() {
  order.effectiveTime = null
  //saveCreatedFrom();
}

/*function saveCreatedFrom() {
  emit('updateCreatedFrom', order.effectiveTime)
}*/


/* ---------- Calculations ---------- */
function recalc() {
  const amt = parseFloat(order.amount || 0)
  const prc = parseFloat(order.price || 0)

  // Store the value without decimal places (numeric string)
  order.value = (amt && prc) ? (amt * prc).toString() : ''
}


/*
  * Only digits and a period are allowed if needed.
  * If `maxDecimals` is 0, periods are not allowed.
 */
function sanitizeNumber(raw) {
  let s = raw.replace(/,/g, '')           // Remove commas
  // Let there be only one dot and remove all other non-numeric characters.
  s = s.replace(/[^0-9.]/g, '')

  // Only one point should be kept.
  const parts = s.split('.')
  if (parts.length > 2) {
    s = parts[0] + '.' + parts.slice(1).join('')
  }

  // No limit on the number of digits after the decimal point.
  return s
}


/* ---------- Deactivating prices ---------- */
//const priceDisabled = computed(() => activeField.value === 'tomanPrice')
//const tomans = computed(() => activeField.value === 'price')

/* ---------- Initial fetch ---------- */
async function fetchInitialData() {
  loading.value = true

  // 1) All GameAccounts
  await ordersStore.action_getGameAccounts()

  order.game = selectedGame.value ? selectedGame.value.game : '';

  loading.value = false
}


const showStocksManagerBtn = computed(() =>
    selectedGame.value?.hasStocksManagerService === true
)

/* Object filter: Keep only keys that have the value truthy */
function buildPayload() {
  const payload = {}
  Object.entries(order).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined) payload[k] = v
  })
  if (attachments.value.length) {
    payload.attachments = {paymentReceipts: attachments.value}
  }
  return payload
}

async function onSave() {
  // 1) Validation (if Vuelidate is available)
  $vulidate.value.$touch?.()
  if ($vulidate.value?.$invalid) {
    selectedTab.value = 0
    return false;
  }

  // 2) Filter filled fields
  const payload = buildPayload()
  if (Object.keys(payload).length === 0) {
    showMessage('No fields are filled in.', 'warning')
    return
  }

  // 3) User verification
  const ok = await showAlert('You are adding new order?', 'Are you sure?', 'Yes', 'No')
  if (!ok) return

  // 4) API call
  try {
    saving.value = true
    const res = await ordersStore.action_addCurrencyTransferOrder(payload)
    if (res === true) {
      showMessage('New Order added!', 'success')
      backToOrders();
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    saving.value = false
  }
}

function backToOrders() {
  if (route.params.game) {
    router.push({name: 'stocks-servers', params: {game: route.params.game}})
  } else {
    router.push({name: 'stocks-orders'})
  }
}

function onSendToStocksManager() {
  // Here is the logic for calling the API or any action that needs to be taken.
  //console.log('Send to Stocks Manager:', order.id)
}

onMounted(fetchInitialData)

/* ---------- Watch for Game Change ---------- */
watch(
    () => order.game,
    () => onGameChange()
)
</script>

<template>
  <div class="h-full">
    <TabGroup :selectedIndex="selectedTab" @change="(index)=> selectedTab = index">
      <div class="space-y-4 text-white/80 text-sm h-full">
        <!-- Loading mode -->
        <div v-if="loading" class="flex items-center justify-center h-60">
          <Icon icon="eos-icons:bubble-loading" class="size-12"/>
        </div>

        <!-- Order details form -->
        <TabPanels v-else class="h-full">
          <div class="grid grid-cols-5 gap-6 h-full">
            <div class="col-span-4 flex flex-col gap-4 relative h-full overflow-hidden px-2">
              <div class="flex flex-row gap-2 items-center justify-between">
                <div class="flex flex-row gap-2 items-center">
                  <Tooltip placement="top" arrow>
                    <template #button>
                      <div class="back-btn" @click="backToOrders">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                          <path fill="currentColor"
                                d="m7.825 13l4.9 4.9q.3.3.288.7t-.313.7q-.3.275-.7.288t-.7-.288l-6.6-6.6q-.15-.15-.213-.325T4.426 12t.063-.375t.212-.325l6.6-6.6q.275-.275.688-.275t.712.275q.3.3.3.713t-.3.712L7.825 11H19q.425 0 .713.288T20 12t-.288.713T19 13z"/>
                        </svg>
                      </div>
                    </template>
                    <span>{{ $t('message.back') }}</span>
                  </Tooltip>
                  <TabList class="">
                    <Tab
                        v-slot="{ selected }"
                        as="template"
                        v-for="(item, i) in tabs"
                        :key="i"
                    >
                      <button
                          :class="[
                            selected
                              ? 'text-white bg-primary-700'
                              : 'text-slate-500 bg-white dark:bg-slate-700 dark:text-slate-300',
                          ]"
                          class="text-sm font-medium last:mb-0 capitalize ring-0 foucs:ring-0 focus:outline-none px-6 py-2 transition duration-150 rounded-none first:rounded-l-2xl  last:rounded-r-2xl  "
                      >
                        {{ item.title }}
                      </button>
                    </Tab>
                  </TabList>
                </div>
              </div>
              <hr class="border-0 h-0.5 bg-slate-800">
              <TabPanel :unmount="false"
                        class="flex-1 flex flex-col gap-6 relative h-full overflow-auto px-2 custom-scrollbar-2">
                <div class="flex flex-row items-start gap-4">
                  <!-- ================== Game ================== -->
                  <div class="flex flex-col gap-0.5 text-sm w-max">
                    <label for="game">Game:</label>
                    <div
                        :class="{ '!border !border-red-300': $vulidate.game.$error }"
                        class="flex flex-row gap-2 items-center w-full bg-slate-900 border border-slate-700 text-slate-300 px-1 rounded-md h-[40px]">
                      <select id="game"
                              v-model="order.game"
                              @change="onGameChange"
                              class="bg-slate-900 h-full focus:ring-0 focus-visible:ring-0 focus-visible:outline-none">
                        <option value="" disabled>-- Select Game --&nbsp</option>
                        <option v-for="g in games" :key="g.game" :value="g.game">{{ g.game }}</option>
                      </select>
                    </div>
                    <template v-if="$vulidate.game.$error">
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.game.required.$response">
                        This field is required
                      </p>
                    </template>
                  </div>

                  <!-- ================== Server ================== -->
                  <div class="flex flex-col gap-0.5 text-sm">
                    <label for="server">Server:</label>
                    <div @click="openServersModal"
                         :class="[
                            servers.length > 0 ? 'cursor-pointer' : 'cursor-default',
                            { '!border !border-red-300': $vulidate.server.$error }
                          ]"
                         class="flex flex-row gap-2 items-center w-full bg-slate-900 border border-slate-700 text-slate-300 px-2 rounded-md h-[40px]">
                      <span v-if="order.server">{{ order.server }}</span>
                      <span v-else class="text-slate-400"
                            :class="servers.length > 0 ? 'cursor-pointer' : 'cursor-default'">-- Select Server --</span>
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                        <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                              stroke-width="2" d="m7 10l5 5m0 0l5-5"/>
                      </svg>
                    </div>
                    <template v-if="$vulidate.server.$error">
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.server.required.$response">
                        This field is required
                      </p>
                    </template>
                  </div>

                  <!-- ================== Account ================== -->
                  <div class="flex flex-col gap-0.5 text-sm w-max">
                    <label for="account">Account:</label>
                    <div
                        :class="{ '!border !border-red-300': $vulidate.account.$error }"
                        class="flex flex-row gap-2 items-center w-full bg-slate-900 border border-slate-700 text-slate-300 px-1 rounded-md h-[40px]">
                      <select id="account"
                              v-model="order.account"
                              :disabled="accounts.length === 0"
                              class="bg-slate-900 h-full focus:ring-0 focus-visible:ring-0 focus-visible:outline-none w-full">
                        <option value="" disabled class="disabled:bg-slate-900 disabled:hover:bg-slate-900">--
                          Select
                          Account
                          --
                          &nbsp
                        </option>
                        <option
                            v-for="acc in accounts"
                            :key="acc.id"
                            :value="acc.id"
                        >
                          {{ acc.title }}&nbsp
                        </option>
                      </select>
                    </div>
                    <template v-if="$vulidate.account.$error">
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.account.required.$response">
                        This field is required
                      </p>
                    </template>
                  </div>

                  <!-- ================== Side ================== -->
                  <div class="flex flex-col gap-0.5 text-sm w-max">
                    <label for="account">Side:</label>
                    <div class="inline-block w-max relative" ref="sideDropdownRef">
                      <button @click="toggleSideDropdown"
                              :class="{ '!border !border-red-300': $vulidate.side.$error }"
                              class="w-full bg-slate-900 border border-slate-700 text-slate-300 rounded-md px-2 py-2 text-left flex justify-between items-center flex-row gap-2">
                        <div class="flex items-center gap-2">
                          <span class="w-2 h-2 rounded-full"
                                :class="order.side ? sidesColorsMap[order.side] : 'bg-[#9E9E9E]'"></span>
                          {{ order.side ? order.side : 'Not Selected' }}
                        </div>
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                        </svg>
                      </button>
                      <div v-if="isSideOpen"
                           class="absolute right-0 z-10 mt-1 w-max min-w-full bg-slate-900 border border-slate-700 text-slate-300 rounded-md shadow-lg">
                        <ul>
                          <li v-for="side in sides" :key="side" @click="selectSide(side)"
                              class="px-2 py-2 hover:bg-slate-800 cursor-pointer flex items-center gap-2">
                            <span class="w-2 h-2 rounded-full" :class="sidesColorsMap[side]"></span>
                            {{ side }}
                          </li>
                        </ul>
                      </div>
                      <template v-if="$vulidate.side.$error">
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.side.required.$response">
                          This field is required
                        </p>
                      </template>
                    </div>
                  </div>
                </div>
                <div class="grid grid-cols-12 gap-4">
                  <!-- ================== Amount ================== -->
                  <div class="col-span-3 flex-1 flex flex-col gap-0.5 text-sm">
                    <label for="amount">Amount:</label>
                    <div class="">
                      <input id="amount"
                             type="text"
                             :value="order.amount"
                             @keydown="allowDigitsDot"
                             @input="onAmountInput($event.target.value)"
                             :class="{ '!border !border-red-300': $vulidate.amount.$error }"
                             class="input-control w-full block focus:outline-none h-[40px] rounded-md read-only:!bg-slate-200 read-only:dark:!text-black-300 read-only:!text-black-900 read-only:dark:!bg-slate-800">
                    </div>
                    <template v-if="$vulidate.amount.$error">
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.amount.required.$response">
                        This field is required
                      </p>
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.amount.numeric.$response">
                        This field should be a number
                      </p>
                    </template>
                  </div>

                  <!-- ================== Price (USD) ================== -->
                  <div class="col-span-3 flex-1 flex flex-col gap-0.5 text-sm">
                    <label for="price">Price:</label>
                    <div class="">
                      <input id="price"
                             type="text"
                             :value="order.price"
                             @keydown="allowDigitsDot"
                             @input="onPriceInput($event.target.value)"
                             :class="{ '!border !border-red-300': $vulidate.price.$error }"
                             class="input-control w-full block focus:outline-none h-[40px] rounded-md read-only:!bg-slate-200 read-only:dark:!text-black-300 read-only:!text-black-900 read-only:dark:!bg-slate-800">
                    </div>
                    <template v-if="$vulidate.price.$error">
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.price.required.$response">
                        This field is required
                      </p>
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.price.numeric.$response">
                        This field should be a number
                      </p>
                    </template>
                  </div>

                  <!-- ================== Value (USD) ================== -->
                  <div class="col-span-3 flex-1 flex flex-col gap-0.5 text-sm">
                    <label for="value">Value:</label>
                    <div class="">
                      <input type="text" id="value"
                             :value="formatNumber(order.value, 2) + ' $'"
                             disabled
                             class="input-control w-full block focus:outline-none h-[40px] rounded-md read-only:!bg-slate-200 read-only:dark:!text-black-300 read-only:!text-black-900 read-only:dark:!bg-slate-800">
                    </div>
                  </div>

                  <!-- ================== Effective Time (USD) ================== -->
                  <div class="col-span-3 flex-1 flex flex-col gap-0.5 text-sm">
                    <div class="flex flex-col gap-0.5">
                      <label for="effectiveTime">Effective Time:</label>
                      <div class="flex flex-row gap-2 items-center">
                        <DatePicker
                            id="effectiveTime"
                            v-model="effectiveTimeModel"
                            showTime
                            hourFormat="24"
                            dateFormat="d M yy"
                            fluid
                            :showButtonBar="true"
                            class="flex-1"
                            size="small"
                            :input-class="{
                              '!border-slate-700': !$vulidate.effectiveTime.$error,
                              'effectiveTimeError': $vulidate.effectiveTime.$error,
                              '!bg-transparent': true,
                              'h-[40px]': true
                            }"
                            ref="pickerRef"
                        >
                          <template #todaybutton>
                            <span @click="selectNow"
                                  class="cursor-pointer px-2 py-1 rounded-md text-xs hover:bg-gray-700/50">Now</span>
                          </template>
                          <template #clearbutton>
                            <span @click="clearEffectiveTime"
                                  class="cursor-pointer px-2 py-1 rounded-md text-xs hover:bg-gray-700/50">Clear</span>
                          </template>
                        </DatePicker>
                      </div>
                    </div>
                    <template v-if="$vulidate.effectiveTime.$error">
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.effectiveTime.required.$response">
                        This field is required
                      </p>
                    </template>
                  </div>
                </div>
                <div class="flex-1 flex flex-col gap-0.5 items-start">
                  <label for="notes">Notes:</label>
                  <div class="w-full h-full">
                    <textarea v-model="order.notes" type="text" id="notes" rows="5"
                              oninput="this.style.height = ''; this.style.height = this.scrollHeight + 3 + 'px';"
                              class="input-control resize-none w-full block min-h-[40px] focus:outline-none rounded-md read-only:!bg-slate-200 read-only:dark:!text-black-300 read-only:!text-black-900 read-only:dark:!bg-slate-800 p-2">
                    </textarea>
                  </div>
                </div>
              </TabPanel>
              <hr class="border-0 h-0.5 bg-slate-800">
              <div class="flex flex-row gap-4 justify-end items-center">
                <button @click="onSave" class="btn btn-outline-primary py-2" :disabled="saving">
                  <span v-if="saving"
                        class="animate-[spin_2s_linear_infinite] inline-block align-middle ltr:mr-2 rtl:ml-2 shrink-0">
                    <Loading/>
                  </span>
                  Save
                </button>
              </div>
            </div>
            <div class="col-span-1 h-full overflow-auto custom-scrollbar-2 rounded-lg pr-2">
              <div class="bg-slate-700 p-2 flex flex-col gap-10 rounded-lg overflow-hidden">
                <h6 class="text-center text-base py-2 bg-slate-900 rounded-lg">New Transfer</h6>
                <div class="flex flex-col gap-1 h-max">
                  <p>Game: {{ order.game }}</p>
                  <p>Server: {{ order.server }}</p>
                  <p>Account: {{ accountTitle }}</p>
                </div>
              </div>
            </div>
          </div>
        </TabPanels>
      </div>
    </TabGroup>
    <serversModal v-if="isShowServersModal" :servers="servers" @close-modal="closeServersModal"
                  @select-server="selectServer"></serversModal>
  </div>
</template>

<style>
.input {
  @apply w-full px-3 py-2 border border-gray-700 rounded-md focus:outline-none focus:ring focus:border-blue-500;
}

.back-btn {
  @apply h-8 w-8 bg-slate-200 dark:bg-slate-700 dark:text-slate-200 text-slate-600 flex flex-col justify-center items-center text-base rounded-full cursor-pointer;
}

.p-datepicker-header {
  @apply !bg-slate-800
}

.p-datepicker-day-view {
  @apply !text-sm;
}

.p-datepicker-weekday-cell {
  @apply text-center
}

.p-datepicker-panel {
  @apply !bg-slate-800 border border-slate-700
}

.p-inputtext.effectiveTimeError {
  @apply !border !border-red-300;
}

.p-inputtext:not(.effectiveTimeError):enabled:focus {
  @apply !border-slate-700;
}
</style>
